import React, { useState } from "react";
import { StyleSheet, View, TouchableOpacity, ScrollView } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import {
  HeadCircuitIcon,
  ArrowsClockwiseIcon,
  FileTextIcon,
  LinkIcon,
  SparkleIcon,
  FolderUserIcon,
} from "@phosphor-icons/react";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import ActivityCard from "./ActivityCard";

const ChooseActivity = ({ onSelect, onCancel, activeModuleData }) => {
  const [selectedId, setSelectedId] = useState(null);
  const { isLarge } = useResponsiveScreen();

  const hasAiTutor = activeModuleData?.aiTutors?.length > 0;
  const assessmentHasQuestions = activeModuleData?.assessment?.aiTutors?.length > 0;
  const hasReview = activeModuleData?.reviews?.length > 0;

  const activityTypes = [
    {
      id: 1,
      title: "Interactive Learning Experience",
      type: "Interactive Learning Experience",
      description:
        "Facilitate AI-driven Socratic dialogue with Axio. Provide an activity description, learning objectives, and helpful context to guide students.",
      icon: <SparkleIcon width={36} height={36} fill="#3E68FE" />,
    },
    {
      id: 2,
      title: "Resource",
      type: "Resource",
      description:
        "Share materials by uploading files like PDFs or slide decks, or link to external resources such as videos.",
      icon: <LinkIcon width={36} height={36} fill="#3E68FE" />,
    },
    {
      id: 3,
      title: "Assignment",
      type: "Assignment",
      description:
        "Set a due date and provide instructions for student work. Optionally upload files or link videos to support the task.",
      icon: <FileTextIcon width={36} height={36} fill="#3E68FE" />,
    },
    {
      id: 4,
      title: "Assessment",
      type: "Assessment",
      description: assessmentHasQuestions
        ? "Assessment already exists for this module."
        : hasAiTutor
          ? "Test your students knowledge with an assessment created from existing Interactive Learning Experiences."
          : "Assessment requires at least one Interactive Learning Experience to be created first.",
      icon: <HeadCircuitIcon width={36} height={36} fill="#3E68FE" />,
    },
    {
      id: 5,
      title: "Review",
      type: "Review",
      description: hasReview
        ? "Review already exists for this module."
        : "Create a review activity for an assessment, allowing students to review their assessment results and related materials.",
      icon: <ArrowsClockwiseIcon width={36} height={36} fill="#3E68FE" />,
    },
    {
      id: 6,
      title: "Project",
      type: "Project",
      description:
        "Create a project template with defined scope, learning outcomes, timeline, and required resources for students to propose against.",
      icon: <FolderUserIcon width={36} height={36} fill="#3E68FE" />,
    },
  ];

  const disabledIds = [];
  if (!hasAiTutor || assessmentHasQuestions) {
    disabledIds.push(4);
  }
  if (hasReview) {
    disabledIds.push(5);
  }

  const handleCardPress = (id) => {
    if (!disabledIds.includes(id)) {
      setSelectedId(id);
    }
  };

  const handleNext = () => {
    if (selectedId) {
      const selectedType = activityTypes.find((type) => type.id === selectedId);
      onSelect(selectedType.type);
    }
  };

  return (
    <View style={styles.container} id="choose-activity-container">
      <View style={styles.header}>
        <CustomText text="Choose an Activity Type" size="xl" weight="bold" />
      </View>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
        tabIndex={0}>
        <View style={styles.grid}>
          {isLarge ? (
            <>
              <View style={styles.row}>
                {activityTypes.slice(0, 3).map((type) => {
                  const isDisabled = disabledIds.includes(type.id);
                  return (
                    <ActivityCard
                      key={type.id}
                      id={type.id}
                      title={type.title}
                      description={type.description}
                      icon={type.icon}
                      selectedId={selectedId}
                      isDisabled={isDisabled}
                      handleCardPress={handleCardPress}
                      width={"calc(33.333% - 13.33px)"}
                    />
                  );
                })}
              </View>
              <View style={styles.row}>
                {activityTypes.slice(3, 6).map((type) => {
                  const isDisabled = disabledIds.includes(type.id);
                  return (
                    <ActivityCard
                      key={type.id}
                      id={type.id}
                      title={type.title}
                      description={type.description}
                      icon={type.icon}
                      selectedId={selectedId}
                      isDisabled={isDisabled}
                      handleCardPress={handleCardPress}
                      width={"calc(33.333% - 13.33px)"}
                    />
                  );
                })}
              </View>
            </>
          ) : (
            <>
              <View style={styles.row}>
                {activityTypes.slice(0, 2).map((type) => {
                  const isDisabled = disabledIds.includes(type.id);
                  return (
                    <ActivityCard
                      key={type.id}
                      id={type.id}
                      title={type.title}
                      description={type.description}
                      icon={type.icon}
                      selectedId={selectedId}
                      isDisabled={isDisabled}
                      handleCardPress={handleCardPress}
                      width={"calc(50% - 10px)"}
                    />
                  );
                })}
              </View>
              <View style={styles.row}>
                {activityTypes.slice(2, 4).map((type) => {
                  const isDisabled = disabledIds.includes(type.id);
                  return (
                    <ActivityCard
                      key={type.id}
                      id={type.id}
                      title={type.title}
                      description={type.description}
                      icon={type.icon}
                      selectedId={selectedId}
                      isDisabled={isDisabled}
                      handleCardPress={handleCardPress}
                      width={"calc(50% - 10px)"}
                    />
                  );
                })}
              </View>
              <View style={styles.row}>
                {activityTypes.slice(4, 6).map((type) => {
                  const isDisabled = disabledIds.includes(type.id);
                  return (
                    <ActivityCard
                      key={type.id}
                      id={type.id}
                      title={type.title}
                      description={type.description}
                      icon={type.icon}
                      selectedId={selectedId}
                      isDisabled={isDisabled}
                      handleCardPress={handleCardPress}
                      width={"calc(50% - 10px)"}
                    />
                  );
                })}
              </View>
            </>
          )}
        </View>
      </ScrollView>
      <View style={styles.footer}>
        <CustomButton
          text="Cancel"
          styleType="primaryTransparentStroke"
          onPress={onCancel}
          textStyle={styles.buttonText}
          style={styles.button}
          size="l"
        />
        <CustomButton
          text="Next"
          textStyle={styles.buttonText}
          styleType="primary"
          onPress={handleNext}
          disabled={!selectedId}
          style={styles.button}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#ffffff",
    borderRadius: 20,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    maxHeight: "80vh",
  },
  header: {
    backgroundColor: "#F2F4F7",
    paddingVertical: 32,
    paddingHorizontal: 32,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  grid: {
    paddingHorizontal: 32,
    paddingTop: 20,
  },
  scrollView: {
    flex: 1,
    maxHeight: "60vh",
  },
  scrollContent: {
    paddingBottom: 20,
  },
  row: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 20,
    gap: 20,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 40,
    paddingHorizontal: 32,
  },
  button: {
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: 600,
  },
});

export default ChooseActivity;
